import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cn } from "../utils";
import { Icon } from "../icon";
import { textButtonVariants } from "./text-button.variants";
import type { TextButtonProps } from "./text-button.types";

/**
 * Text Button Component
 *
 * V&A Waterfront Design System - Text Button
 * Figma: https://www.figma.com/design/CV2ePEn9S0SSZ7T3AAPqF4/V-A-Waterfront---Website---Foundation-Design-System?node-id=9858-7316
 *
 * A lightweight link-style button for secondary actions with underline on hover.
 * Includes a trailing arrow icon by default that animates on hover.
 *
 * @example
 * // Basic usage
 * <TextButton>Learn more</TextButton>
 *
 * // Secondary variant
 * <TextButton variant="secondary">View details</TextButton>
 *
 * // Without default arrow icon
 * <TextButton iconRight={null}>Read article</TextButton>
 *
 * // With custom icons
 * <TextButton iconLeft="chevron-left" iconRight="external-link">Open in new tab</TextButton>
 *
 * // As anchor
 * <TextButton asChild>
 *   <a href="/page">Navigate</a>
 * </TextButton>
 */
const TextButton = React.forwardRef<HTMLButtonElement, TextButtonProps>((t0, ref) => {
  const $ = _c(34);
  let children;
  let className;
  let disabled;
  let iconLeft;
  let props;
  let size;
  let t1;
  let t2;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      asChild: t1,
      iconLeft,
      iconRight: t2,
      children,
      disabled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = disabled;
    $[4] = iconLeft;
    $[5] = props;
    $[6] = size;
    $[7] = t1;
    $[8] = t2;
    $[9] = variant;
  } else {
    children = $[1];
    className = $[2];
    disabled = $[3];
    iconLeft = $[4];
    props = $[5];
    size = $[6];
    t1 = $[7];
    t2 = $[8];
    variant = $[9];
  }
  const asChild = t1 === undefined ? false : t1;
  const iconRight = t2 === undefined ? "arrow-right" : t2;
  const Comp = asChild ? Slot : "button";
  const t3 = asChild ? undefined : "button";
  let t4;
  if ($[10] !== className || $[11] !== size || $[12] !== variant) {
    t4 = cn(textButtonVariants({
      variant,
      size,
      className
    }));
    $[10] = className;
    $[11] = size;
    $[12] = variant;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const t5 = asChild ? undefined : disabled;
  const t6 = disabled || undefined;
  const t7 = disabled || undefined;
  const t8 = disabled ? -1 : undefined;
  let t9;
  if ($[14] !== iconLeft) {
    t9 = iconLeft && <Icon name={iconLeft} />;
    $[14] = iconLeft;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== asChild || $[17] !== children) {
    t10 = asChild ? <Slottable>{children}</Slottable> : <span>{children}</span>;
    $[16] = asChild;
    $[17] = children;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== iconRight) {
    t11 = iconRight && <Icon name={iconRight} className="transition-transform group-hover:translate-x-0.5" />;
    $[19] = iconRight;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== Comp || $[22] !== props || $[23] !== ref || $[24] !== t10 || $[25] !== t11 || $[26] !== t3 || $[27] !== t4 || $[28] !== t5 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t12 = <Comp ref={ref} data-slot="text-button" type={t3} className={t4} disabled={t5} aria-disabled={t6} data-disabled={t7} tabIndex={t8} {...props}>{t9}{t10}{t11}</Comp>;
    $[21] = Comp;
    $[22] = props;
    $[23] = ref;
    $[24] = t10;
    $[25] = t11;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
});
TextButton.displayName = "TextButton";
export { TextButton };